import { Box, Grid, Stack, Typography } from '@mui/material';
import { Role, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo, useState } from 'react';
import { getKeyManagersAssignedToGroup, getVisitorGroupById } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import useVisitorsGroups from 'v2/hooks/useVisitorsGroups/useVisitorsGroups';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import AddKeyManagerSearch from './AddKeyManagerSearch';
import InvitedMemberRow from './InvitedMemberRow';
import KeyManagerRow from './KeyManagerRow';

interface Props {
  visitorGroupId: string;
}

const EditKeySharingDrawer = ({ visitorGroupId }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();
  const { setVisitorByIdWithRole } = useExternalUsers({
    translation,
    handleFetchError: setError
  });
  const { allVisitorGroups, loadingAllVisitorGroups } = useVisitorsGroups({
    handleFetchError: setError
  });

  // visitorId -> desired role, only for members toggled since opening the drawer.
  const [pendingRoles, setPendingRoles] = useState<Record<string, Role>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const visitorGroup = useMemo(
    () => getVisitorGroupById(allVisitorGroups, visitorGroupId),
    [allVisitorGroups, visitorGroupId]
  );

  // Applies pendingRoles on top of visitorGroup so the UI reflects unsaved toggles.
  const effectiveVisitorGroup = useMemo(() => {
    if (!visitorGroup || Object.keys(pendingRoles).length === 0) return visitorGroup;
    return {
      ...visitorGroup,
      members: visitorGroup.members.map((member) => {
        const pendingRole = pendingRoles[member.id];
        if (!pendingRole) return member;
        return {
          ...member,
          visitorGroupMembership: member.visitorGroupMembership.map((membership) =>
            membership.visitorGroup.id === visitorGroup.id ? { ...membership, role: pendingRole } : membership
          )
        };
      })
    };
  }, [visitorGroup, pendingRoles]);

  // Don't show the full-screen loader during mutation — the overlay handles it.
  const isLoading = loadingAllVisitorGroups || (!isSubmitting && visitorGroup === undefined);

  const keyManagers = useMemo(
    () => (effectiveVisitorGroup ? getKeyManagersAssignedToGroup(effectiveVisitorGroup) : []),
    [effectiveVisitorGroup]
  );
  const keySharingUsers = useMemo<Visitor[]>(
    () =>
      effectiveVisitorGroup
        ? effectiveVisitorGroup.members.filter((m) => getVisitorOrigin(m) === VisitorOrigin.KeySharing)
        : [],
    [effectiveVisitorGroup]
  );
  const promotableMembers = useMemo(
    () => effectiveVisitorGroup?.members.filter((m) => !keyManagers.some((km) => km.id === m.id)),
    [effectiveVisitorGroup, keyManagers]
  );

  const handlePromoteToKeyManager = (member: Visitor) =>
    setPendingRoles((prev) => ({ ...prev, [member.id]: Role.ExternalGroupUserManager }));

  const handleRemoveKeyManager = (member: Visitor) =>
    setPendingRoles((prev) => ({ ...prev, [member.id]: Role.ExternalGroupUser }));

  const handleSave = async () => {
    setError(undefined);
    if (!visitorGroup) {
      showSnackbar(translation.user_group_not_found, { severity: 'error' });
      closeDrawer();
      return;
    }
    const changedMembers = visitorGroup.members.filter((member) => pendingRoles[member.id] !== undefined);
    if (changedMembers.length === 0) {
      closeDrawer();
      return;
    }
    setIsSubmitting(true);
    try {
      for (const member of changedMembers) {
        await setVisitorByIdWithRole({
          visitorId: member.id,
          name: member.name,
          visitorGroupMembership: [
            ...member.visitorGroupMembership
              .filter((membership) => membership.visitorGroup.id !== visitorGroup.id)
              .map((membership) => ({ visitorGroupId: membership.visitorGroup.id, role: membership.role })),
            { visitorGroupId: visitorGroup.id, role: pendingRoles[member.id] }
          ]
        });
      }
      showSnackbar(translation.saved_changes, { severity: 'success' });
      closeDrawer();
    } catch {
      // inline error already set by useExternalUsers' internal setVisitorByIdWithRoleError effect
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.key_sharing}
      primaryAction={{
        label: translation.save,
        onClick: handleSave,
        loading: isLoading || isSubmitting,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      {isLoading ? (
        <Box width="100%" height="100%" justifyContent="center" alignContent="center">
          <Loader />
        </Box>
      ) : (
        <Stack gap={3} mt={1}>
          <Typography variant="body2" color="textSecondary">
            {translation.key_sharing_description}
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="subtitle1">{translation.key_managers}</Typography>
            <Typography variant="body2" color="textSecondary">
              {keyManagers.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="textSecondary">
            {translation.key_manager_search_hint}
          </Typography>
          <AddKeyManagerSearch members={promotableMembers ?? []} onSelect={handlePromoteToKeyManager} />
          <Grid container spacing={0.5} direction="column">
            {keyManagers.map((keyManager, index) => (
              <KeyManagerRow
                key={keyManager.id}
                index={index}
                keyManager={keyManager}
                onRemove={() => handleRemoveKeyManager(keyManager)}
              />
            ))}
          </Grid>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1">{translation.invited_users}</Typography>
            <Typography variant="caption" color="textSecondary">
              {keySharingUsers.length}
            </Typography>
          </Grid>
          <Grid container spacing={0.5} direction="column">
            {keySharingUsers.map((member, index) => (
              <InvitedMemberRow
                key={member.id}
                index={index}
                member={member}
                visitorGroupId={visitorGroupId}
                isKeyManager={keyManagers.some((keyManager) => keyManager.id === member.id)}
                loading={isSubmitting}
                onToggle={(becomeKeyManager) =>
                  becomeKeyManager ? handlePromoteToKeyManager(member) : handleRemoveKeyManager(member)
                }
              />
            ))}
          </Grid>
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default EditKeySharingDrawer;
